import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";

const GLOW = new THREE.Color("#FFA040");

export const Interactive = ({ id, label, hotspot, position, rotation, ui, children }) => {
  const ref = useRef();
  const [hovered, setHovered] = useState(false);
  const { activeId, onSelect, showHotspots, entered, visited = [] } = ui;
  const active = activeId === id;
  const seen = visited.includes(id) && !active;
  const lit = hovered || active;

  useEffect(() => {
    ref.current?.traverse((o) => {
      if (!o.isMesh || !o.material?.emissive) return;
      if (!o.userData.base) o.userData.base = { c: o.material.emissive.clone(), i: o.material.emissiveIntensity };
      const base = o.userData.base;
      const glowing = base.c.getHex() === 0;
      if (lit && glowing) {
        o.material.emissive.copy(GLOW);
        o.material.emissiveIntensity = hovered ? 0.32 : 0.07;
      } else {
        o.material.emissive.copy(base.c);
        o.material.emissiveIntensity = base.i;
      }
    });
  }, [lit, hovered]);

  useEffect(() => {
    if (!hovered) return undefined;
    document.body.style.cursor = "pointer";
    return () => { document.body.style.cursor = ""; };
  }, [hovered]);

  useFrame((_, dt) => {
    const g = ref.current;
    if (!g) return;
    g.position.y = THREE.MathUtils.damp(g.position.y, hovered ? 0.07 : 0, 9, dt);
    g.scale.setScalar(THREE.MathUtils.damp(g.scale.x, hovered ? 1.05 : 1, 9, dt));
  });

  const select = (e) => {
    e.stopPropagation();
    if (e.delta > 6 || !entered) return;
    onSelect(id);
  };

  return (
    <group position={position} rotation={rotation}>
      <group
        ref={ref}
        onPointerOver={(e) => { e.stopPropagation(); if (entered) setHovered(true); }}
        onPointerOut={() => setHovered(false)}
        onClick={select}
      >
        {typeof children === "function" ? children({ hovered, active }) : children}
      </group>
      {entered && (
        <Html position={hotspot} center zIndexRange={[30, 0]}>
          <div className={`hotspot ${hovered ? "is-hover" : ""} ${showHotspots || hovered ? "" : "is-hidden"} ${activeId && !active ? "is-dim" : ""} ${seen ? "is-visited" : ""}`}>
            <button
              data-testid={`object-hotspot-${id}`}
              data-visited={seen ? "true" : "false"}
              aria-label={label}
              className="hotspot-dot"
              onClick={() => onSelect(id)}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            />
            <span className="hotspot-label">{label}</span>
          </div>
        </Html>
      )}
    </group>
  );
};
